import React, { useEffect, useState } from 'react';
import { Alert, Box } from '@mui/material';
import SidebarNavigation, {
    SideBarMenuItemProps,
} from '../../ui/SidebarNavigation';
import {
    Redirect,
    Router,
    Switch,
    useHistory,
    useLocation,
} from 'react-router-dom';
import AuthenticatedRoute from '../../ui/AuthenticatedRoute';
import { publisherRoutes, Route } from '../../../routes';
import { ArticleFilterProvider } from '../../providers/admin/ArticleFilterProvider';
import { ResearchRole } from '../../../shared/global-enums';
import { useUser } from '../../providers/UserProvider';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { premiumChannelsEnabled } from '../../../utils/utils';

interface SideBarMenuItemWithUrl extends SideBarMenuItemProps {
    url: string;
    permittedRoles?: ResearchRole[];
}

const PublisherContent = (): JSX.Element => {
    // TODO: SideBarMenuItemProps doesn't include 'url' key, we should not pass 'url' down to SidebarNavigation component, SideBarMenuItemWithUrl interface is a temporary fix
    const [menuItems, setMenuItems] = useState<SideBarMenuItemWithUrl[]>(
        [
            {
                name: 'Articles',
                url: baseUrls.publisher.articles.list,
            },
            {
                name: 'Templates',
                url: baseUrls.publisher.templates.list,
                permittedRoles: [
                    ResearchRole.Administrator,
                    ResearchRole.Editor,
                    ResearchRole.Contributor,
                ],
            },
            {
                name: 'Channels',
                url: baseUrls.publisher.premiumChannels.list,
                permittedRoles: [
                    ResearchRole.Administrator,
                    ResearchRole.Editor,
                    ResearchRole.Contributor,
                ],
            },
            {
                name: 'Channel Groups',
                url: baseUrls.publisher.channelGroups.list,
                permittedRoles: [ResearchRole.Administrator],
            },
            {
                name: 'User Guide',
                url: baseUrls.publisher.userGuideScopes.list,
                permittedRoles: [ResearchRole.Administrator],
            },
            {
                name: premiumChannelsEnabled() ? 'Topics' : 'Categories',
                url: premiumChannelsEnabled()
                    ? baseUrls.publisher.topics.list
                    : baseUrls.publisher.categories.list,
            },
            {
                name: 'Tags',
                url: baseUrls.publisher.tags.list,
            },
            {
                name: 'Users',
                url: baseUrls.publisher.users.list,
            },
            {
                name: 'Desks',
                url: baseUrls.publisher.desks.list,
            },
            {
                name: 'Promotional Content',
                url: baseUrls.publisher.promotionalContent.list,
                permittedRoles: [ResearchRole.Administrator],
            },
        ].filter((t) =>
            t.name === 'Channels' ? premiumChannelsEnabled(false) : true
        )
    );

    const history = useHistory();
    const location = useLocation();
    const { hasScope } = useUser();

    const onSideBarChange = (menuItem: SideBarMenuItemWithUrl): void => {
        history.push(menuItem.url);
    };

    useEffect(() => {
        setMenuItems((items) =>
            items.map((menuItem) => ({
                name: menuItem.name,
                url: menuItem.url,
                selected:
                    menuItem.url === baseUrls.publisher.promotionalContent.list
                        ? location.pathname.startsWith(menuItem.url) ||
                          location.pathname.startsWith(
                              baseUrls.publisher.promotionalPage.list
                          )
                        : location.pathname.startsWith(menuItem.url),
                permittedRoles: menuItem.permittedRoles,
            }))
        );
    }, [location.pathname]);

    const permittedMenuItems = menuItems.filter((item) => {
        if (item.permittedRoles) {
            return item.permittedRoles.some(hasScope);
        }

        return true;
    });

    return (
        <Box
            sx={{
                display: 'flex',
                flex: '1 1 auto',
                minHeight: 0,
                minWidth: 0,
            }}
        >
            <Box
                sx={{
                    display: { xs: 'none', lg: 'flex' },
                    width: '100%',
                    minHeight: 0,
                }}
            >
                <Box sx={{ display: 'flex', flex: '0 0 280px', minWidth: 0 }}>
                    <SidebarNavigation
                        id={'publisher-sidebar'}
                        menuItems={permittedMenuItems}
                        onChange={(item: SideBarMenuItemProps) => {
                            // TODO: SideBarMenuItemProps doesn't include 'url' key, we should not pass 'url' down to SidebarNavigation component, SideBarMenuItemWithUrl interface is a temporary fix
                            const menuItem: SideBarMenuItemWithUrl =
                                item as SideBarMenuItemWithUrl;
                            onSideBarChange(menuItem);
                        }}
                    />
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        minHeight: 0,
                        minWidth: 0,
                    }}
                >
                    <Box sx={{ width: '100%', height: '100%', minHeight: 0 }}>
                        <ArticleFilterProvider>
                            <Router history={history}>
                                <Switch>
                                    <Redirect
                                        from={baseUrls.publisher.main}
                                        exact
                                        to={baseUrls.publisher.articles.list}
                                    />
                                    {publisherRoutes.map(
                                        (
                                            route: Route<undefined>,
                                            i: number
                                        ) => (
                                            <AuthenticatedRoute
                                                exact
                                                key={i}
                                                {...route}
                                            />
                                        )
                                    )}
                                </Switch>
                            </Router>
                        </ArticleFilterProvider>
                    </Box>
                </Box>
            </Box>
            <Box
                sx={{
                    display: { xs: 'flex', lg: 'none' },
                    flex: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3,
                }}
            >
                <Alert severity="info" sx={{ maxWidth: 520 }}>
                    This application cannot work on small devices. For the best
                    experience, please use a web browser on a desktop or laptop
                    computer.
                </Alert>
            </Box>
        </Box>
    );
};

export default PublisherContent;
